import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Users, TrendingUp, Loader2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface DashboardStats {
  totalProperties: number;
  totalUsers: number;
  totalViews: number;
  totalLeads: number;
  conversionRate: number;
}

export default function AdminDashboardPage() {
  const [stats, setStats] = useState<DashboardStats>({
    totalProperties: 0,
    totalUsers: 0,
    totalViews: 0,
    totalLeads: 0,
    conversionRate: 0,
  });
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);

      // Get total users based on role
      let usersQuery = supabase.from('users').select('*', { count: 'exact', head: true });
      
      // If user is partner, only count users they created
      if (user?.role === 'parceiro') {
        usersQuery = usersQuery.eq('created_by', user.id);
      }
      
      // Fetch total users
      const { count: totalUsers } = await usersQuery;

      // Get all users for this partner/admin
      let userDataQuery = supabase
        .from('users')
        .select('id, niche_type');

      if (user?.role === 'parceiro') {
        userDataQuery = userDataQuery.eq('created_by', user.id);
      }

      const { data: usersData } = await userDataQuery;

      // Initialize counters
      let totalProperties = 0;
      let totalViews = 0;
      let totalLeads = 0;

      // Count properties, views and leads for each user
      for (const userData of usersData || []) {
        // Count properties
        const { count: propertiesCount } = await supabase
          .from('properties')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userData.id);
        totalProperties += propertiesCount || 0;

        // Count property views
        const { count: propertyViews } = await supabase
          .from('property_views')
          .select('*', { count: 'exact', head: true })
          .eq('listing_type', 'property')
          .in('property_id', 
            (await supabase
              .from('properties')
              .select('id')
              .eq('user_id', userData.id)
            ).data?.map(p => p.id) || []
          );
        totalViews += propertyViews || 0;

        // Count property leads
        const { count: propertyLeads } = await supabase
          .from('leads')
          .select('*', { count: 'exact', head: true })
          .eq('listing_type', 'property')
          .in('property_id', 
            (await supabase
              .from('properties')
              .select('id')
              .eq('user_id', userData.id)
            ).data?.map(p => p.id) || []
          );
        totalLeads += propertyLeads || 0;
      }

      // Calculate conversion rate
      const conversionRate = totalViews > 0 ? (totalLeads / totalViews) * 100 : 0;

      setStats({
        totalProperties,
        totalUsers: totalUsers || 0,
        totalViews,
        totalLeads,
        conversionRate,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Painel Administrativo</h1>
          {user?.role === 'parceiro' && (
            <p className="text-muted-foreground mt-1">
              Gerencie seus corretores e acompanhe as estatísticas
            </p>
          )}
        </div>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {user?.role === 'parceiro' ? 'Total de Imóveis' : 'Total de Usuários'}
            </CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {user?.role === 'parceiro' ? stats.totalProperties : stats.totalUsers}
            </div>
            <p className="text-xs text-muted-foreground">
              {user?.role === 'parceiro' 
                ? 'Imóveis cadastrados' 
                : 'Usuários no sistema'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {user?.role === 'parceiro' ? 'Total de Corretores' : 'Total de Visualizações'}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {user?.role === 'parceiro' ? stats.totalUsers : stats.totalViews}
            </div>
            <p className="text-xs text-muted-foreground">
              {user?.role === 'parceiro' 
                ? 'Corretores gerenciados' 
                : 'Visualizações totais'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total de Visualizações
            </CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalViews}</div>
            <p className="text-xs text-muted-foreground">
              Visualizações dos imóveis
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Leads</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalLeads}</div>
            <p className="text-xs text-muted-foreground">
              Contatos recebidos
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Conversion Rate Card */}
      <div className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Taxa de Conversão
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-primary mb-2">
              {stats.conversionRate.toFixed(1)}%
            </div>
            <p className="text-muted-foreground">
              Taxa de conversão de visualizações para leads
            </p>
            <div className="mt-4 text-sm text-muted-foreground">
              <p>• {stats.totalViews} visualizações totais</p>
              <p>• {stats.totalLeads} leads gerados</p>
              <p>• {stats.totalUsers} usuários {user?.role === 'parceiro' ? 'gerenciados' : 'no sistema'}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}